import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cls from 'classnames';
import intl from 'react-intl-universal';

import { RoomMainStatus } from '@/config/constant';
import { MONTH_FORMAT } from '@/config/reg';
import DateJS, { format2TimeStamp } from '@/utils/date';
import { getTimeByUtcOffset } from '@/utils/intl';
import { getRoomCalendar } from '@/api/roomState';
import useSafeState from '@/hooks/useSafeState';

import styles from './index.module.scss';

const WEEK_ROW_COUNT = 6;
const WEEK_DAY_COUNT = 7;
const weekFirstDay = 0;

const getViewStartDate = (viewDate) => {
  const monthStartDate = DateJS(viewDate).date(1); // 月1号
  const startDateWeekDay = monthStartDate.day(); // 1号星期几
  // 开始日期 向前推 当月第一天在周第一天之前时 需要向前推一周
  const viewStartDate = monthStartDate.clone().add(weekFirstDay - startDateWeekDay, 'day');
  if (viewStartDate.month() === monthStartDate.month() && viewStartDate.date() > 1) {
    return viewStartDate.add(-WEEK_DAY_COUNT, 'day');
  }
  return viewStartDate;
};

export const CalendarBody = (props) => {
  const { data, addDateByMonth } = props;
  const bodyRef = useRef();

  const calendarHeader = useMemo(() => {
    const headerCells = [];
    const WEEK_DAYS = DateJS.weekdaysMin();
    for (let colIndex = 0; colIndex < WEEK_DAY_COUNT; colIndex++) {
      const cellText = WEEK_DAYS[(colIndex + weekFirstDay) % WEEK_DAY_COUNT];
      headerCells.push(<td key={colIndex}>{cellText}</td>);
    }
    return (
      <thead className={cls(styles.calendarHeader, 'custom-calendar-header')}>
        <tr>{headerCells}</tr>
      </thead>
    );
  }, []);

  const calendarBody = useMemo(() => {
    const bodyRows = [];
    const cellName = cls(styles.bodyCellInner, styles.cellInner);
    for (let rowIndex = 0; rowIndex < WEEK_ROW_COUNT; rowIndex++) {
      const rowCells = [];
      for (let colIndex = 0; colIndex < WEEK_DAY_COUNT; colIndex++) {
        const { dateText, timestamp, cellProps } = data[rowIndex * WEEK_DAY_COUNT + colIndex];
        rowCells.push(
          <td key={timestamp} {...cellProps}>
            <span className={styles.cellBgArea} />
            <span className={cellName}>{dateText}</span>
          </td>,
        );
      }
      bodyRows.push(<tr key={rowIndex}>{rowCells}</tr>);
    }
    return <tbody className={cls(styles.calendarBody, 'custom-calendar-body')}>{bodyRows}</tbody>;
  }, [data]);

  useEffect(() => {
    const el = bodyRef.current || null;
    let pendingTimer;
    const delay = 300;
    const handler = (e) => {
      if (e.ctrlKey) return;
      e.preventDefault();
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        pendingTimer = setTimeout(() => {
          pendingTimer = null;
        }, delay);
        return;
      }
      pendingTimer = setTimeout(() => {
        pendingTimer = null;
      }, delay);
      addDateByMonth(e.deltaY > 0 ? 1 : -1);
    };
    if (el) {
      el.addEventListener('wheel', handler);
    }
    return () => {
      if (el) {
        el.removeEventListener('wheel', handler);
      }
    };
  }, []);

  return (
    <div className={cls(styles.customCalendar, 'component-custom-calendar')} ref={bodyRef}>
      <table className={cls(styles.calendarTable, 'custom-calendar-table')}>
        {calendarHeader}
        {calendarBody}
      </table>
    </div>
  );
};

export const CalendarHeader = (props) => {
  const { date, addDateByMonth } = props;
  const setToPrevMonth = () => addDateByMonth(-1);
  const setToNextMonth = () => addDateByMonth(1);

  return (
    <div className={styles.datePickerHeader}>
      <button type="button" className={styles.headerItemBtn} onClick={setToPrevMonth}>
        <span className={cls(styles.headerItemIcon, styles.iconPrevMonth)} />
      </button>
      <div className={styles.headerItemDate}>
        <span className={styles.headerDateYear}>{getTimeByUtcOffset(date, MONTH_FORMAT)}</span>
      </div>
      <button type="button" className={styles.headerItemBtn} onClick={setToNextMonth}>
        <span className={cls(styles.headerItemIcon, styles.iconNextMonth)} />
      </button>
    </div>
  );
};

const getViewData = (viewDate) => {
  const startDate = getViewStartDate(viewDate);
  const totalDay = WEEK_ROW_COUNT * WEEK_DAY_COUNT;
  let idx = 0;
  const dataList = [];
  while (idx < totalDay) {
    const itemDate = startDate.clone().add(idx, 'day');
    dataList.push({
      timestamp: format2TimeStamp(itemDate),
      dateText: itemDate.date(),
      date: itemDate,
    });
    idx += 1;
  }
  return dataList;
};

const Calendar: React.FC<{ roomId: number; businessDate: number }> = (props) => {
  const { roomId, businessDate } = props;

  const [monthIndex, setMonthIndex] = useState(0); // 0 - 5
  const [viewData, setViewData] = useSafeState(() => {
    const monthStart = DateJS().startOf('day');
    const dataList = getViewData(monthStart);
    return {
      dataList,
      viewDate: monthStart,
    };
  });

  const addDateByMonth: (value: number) => void = useCallback((value) => {
    setMonthIndex((index) => {
      const target = index + value;
      if (target < 0 || target > 5) return index;
      return target;
    });
  }, []);

  useEffect(() => {
    (async () => {
      const monthStart = DateJS(businessDate).add(monthIndex, 'month').startOf('day');
      const dataList = getViewData(monthStart);
      const startItem = dataList[0];
      const endItem = dataList[dataList.length - 1];
      try {
        const res = await getRoomCalendar({
          roomId,
          startDate: startItem.timestamp,
          endDate: endItem.timestamp,
        });
        const calendarMap = {};
        (res.data || []).forEach((item) => {
          calendarMap[item.date] = item;
        });
        const updateViewData = [];
        const viewMonth = monthStart.month();

        // 指定日期的房态信息 默认为空房
        const getCellData = (date) => {
          return calendarMap[date] || { mainStatus: RoomMainStatus.Vacant, recNo: null };
        };

        dataList.forEach((item) => {
          const { timestamp, date, dateText } = item;
          const { mainStatus, recNo } = getCellData(timestamp);
          const prevData = getCellData(format2TimeStamp(date.clone().subtract(1, 'day'))); // 前一天
          const nextData = getCellData(format2TimeStamp(date.clone().add(1, 'day'))); // 后一天
          updateViewData.push({
            timestamp,
            date,
            dateText:
              timestamp === businessDate ? intl.get('roomstate_future_constant_today') : dateText,
            cellProps: {
              className: cls({
                [styles.cellInView]: date.month() === viewMonth,
                // 预订状态
                [styles.reserveRange]: mainStatus === RoomMainStatus.Reserved,
                // 入住状态
                [styles.occupiedRange]: mainStatus === RoomMainStatus.Occupied,
                // 退房状态
                [styles.checkOutRange]: mainStatus === RoomMainStatus.CheckOut,
                // 停用状态
                [styles.outServiceRange]: mainStatus === RoomMainStatus.OutService,
                // 和前一天状态不一致 或者 和前一天单号不一致 表示开始
                [styles.cellRangeStart]:
                  mainStatus !== prevData.mainStatus || recNo !== prevData.recNo,
                // 和后一天状态不一致 或者 和后一天单号不一致 表示结束
                [styles.cellRangeEnd]:
                  mainStatus !== nextData.mainStatus || recNo !== nextData.recNo,
              }),
            },
          });
        });
        setViewData({
          dataList: updateViewData,
          viewDate: monthStart,
        });
      } catch (_) {
        //
      }
    })();
  }, [monthIndex, businessDate]);

  return (
    <div className={styles.datePickerContainer}>
      <CalendarHeader date={viewData.viewDate} addDateByMonth={addDateByMonth} />
      <CalendarBody data={viewData.dataList} addDateByMonth={addDateByMonth} />
    </div>
  );
};

export default Calendar;
